import { useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Link } from 'react-router-dom'
import { isTahaOnly } from '@/features/admin/staff'
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  TrendingUp,
  AlertCircle,
  Brain,
  CheckCircle2,
  XCircle,
  CircleSlash,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { STEP_TYPE_LABELS, STRATEGY_LABELS } from './constants'
import {
  usePreviewMessages,
  useSimulateReply,
  useCachedPreview,
  type PreviewLead,
  type PreviewResponse,
  type SimulatorMessage,
  type SimulateReplyResponse,
  type PreviewAIContextSection,
} from './use-preview'
import { fmtLocale } from '@/lib/dates'

type PreviewMessagesDialogProps = {
  readonly campaignId: string
  readonly campaignName: string
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

// Default fan-out per dialog open. Lowered from 20 → 5 on 2026-06-04 because
// each lead × step = 1 paid Claude call, so one click was minting 60-100
// calls on the org-wide $5/day cap. Five leads is enough to validate the
// AI's voice / personalization quality before activating; users who want
// a fuller preview can ask via Regenerate (still 5, but force-uncached) or
// look at the Inbox after sending. Pair with the PlanGate on the entry
// button so Starter accounts can't fire this at all.
const DEFAULT_LEAD_LIMIT = 5

export function PreviewMessagesDialog({
  campaignId,
  campaignName,
  open,
  onOpenChange,
}: PreviewMessagesDialogProps) {
  // Internal tool — Taha only (owner directive 2026-07-14). The entry button
  // is already gated in campaign-detail; this in-component guard means the
  // dialog renders nothing for anyone else even if it's ever mounted from a
  // new call site. The backend 403s regardless — this is presentation.
  const { user: clerkUser } = useUser()
  const allowed = isTahaOnly(clerkUser?.id)
  const cached = useCachedPreview(open && allowed ? campaignId : null)
  const generate = usePreviewMessages()
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false)
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false)

  const preview = generate.data ?? cached

  // Auto-fire once per dialog open if there's nothing cached. Reset the flag
  // when the dialog closes so a fresh open with no cache triggers again.
  useEffect(() => {
    if (!open) {
      setHasAutoGenerated(false)
      setConfirmingRegenerate(false)
      return
    }
    if (allowed && !preview && !generate.isPending && !hasAutoGenerated) {
      setHasAutoGenerated(true)
      generate.mutate({ campaignId, leadLimit: DEFAULT_LEAD_LIMIT, force: false })
    }
  }, [open, allowed, preview, generate, campaignId, hasAutoGenerated])

  function handleRegenerate() {
    // Two-click confirmation. Regenerate passes force:true which bypasses the
    // KV cache and bills a fresh fan-out — at 5 leads × ~4 steps that's
    // ~20 Claude calls ≈ $0.10 per click on Sonnet. Not huge, but enough
    // that a curious user clicking repeatedly used to single-handedly exhaust
    // the org cap before this confirm step landed.
    if (!confirmingRegenerate) {
      setConfirmingRegenerate(true)
      return
    }
    setConfirmingRegenerate(false)
    generate.mutate({ campaignId, leadLimit: DEFAULT_LEAD_LIMIT, force: true })
  }

  // After all hooks: non-internal users get nothing, ever.
  if (!allowed) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cobalt-bright" />
                AI Message Preview
              </DialogTitle>
              <DialogDescription>
                What the AI would send to leads in <span className="font-medium text-foreground">{campaignName}</span>{' '}
                if they accepted the connection. Nothing is sent. Type a reply on any lead to see how the AI would respond back.
              </DialogDescription>
            </div>
            <Button
              variant={confirmingRegenerate ? 'destructive' : 'outline'}
              size="sm"
              onClick={handleRegenerate}
              disabled={generate.isPending}
              title={confirmingRegenerate ? 'Click again to confirm — this bills a fresh AI fan-out' : 'Regenerate uses the daily AI budget'}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${generate.isPending ? 'animate-spin' : ''}`} />
              {confirmingRegenerate
                ? 'Click again to confirm'
                : preview?.cached
                  ? 'Regenerate'
                  : generate.isPending
                    ? 'Generating…'
                    : 'Generate fresh'}
            </Button>
          </div>
        </DialogHeader>

        <PreviewBody
          campaignId={campaignId}
          preview={preview}
          isPending={generate.isPending}
          error={generate.error instanceof Error ? generate.error.message : null}
        />

        <ImprovementFooter />
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Improvement footer — sits below the previews, tells the user the AI
// keeps learning and points them to the two places they can influence it:
// Voice & Content settings (their samples + voice config) and Support
// (feature requests for specific tuning they want).
// ============================================================

function ImprovementFooter() {
  return (
    <div className="shrink-0 mt-3 flex items-start gap-3 rounded-lg border border-[var(--color-cobalt-bright)]/30 bg-[var(--color-cobalt-bright)]/10 px-4 py-3 text-sm">
      <TrendingUp className="h-5 w-5 mt-0.5 shrink-0 text-[var(--color-cobalt-bright)]" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground mb-0.5">
          Your AI messages keep getting better
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          The AI tunes itself against your reply and close-rate data plus the writing samples you save in your voice settings. Want something specific changed?{' '}
          <Link
            to="/support?new=feature_request"
            className="font-semibold text-[var(--color-cobalt-bright)] underline-offset-4 hover:underline"
          >
            Request it here
          </Link>
          .
        </p>
      </div>
    </div>
  )
}

// ============================================================
// Body — switches between loading / error / empty / list
// ============================================================

function PreviewBody({
  campaignId,
  preview,
  isPending,
  error,
}: {
  readonly campaignId: string
  readonly preview: PreviewResponse | undefined
  readonly isPending: boolean
  readonly error: string | null
}) {
  // For empty/loading/error states, render a single block that grows to fill
  // the flex column. `min-h-0 flex-1` matches the same pattern create-campaign-
  // wizard uses for its scroll body — it works because flex-1 + min-h-0 lets
  // the container shrink AND grow inside the parent flex column.
  if (isPending && !preview) {
    return (
      <div className="min-h-0 flex-1 flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm font-medium">Generating sample messages…</p>
          <p className="text-xs text-muted-foreground max-w-sm">
            Calling the AI for each step on each lead in parallel. Usually 5–15 seconds.
          </p>
        </div>
      </div>
    )
  }

  if (error && !preview) {
    return (
      <div className="min-h-0 flex-1 flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-2 text-center max-w-md">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <p className="text-sm font-medium">Could not generate preview</p>
          <p className="text-xs text-muted-foreground break-words">{error}</p>
        </div>
      </div>
    )
  }

  if (!preview) {
    return (
      <div className="min-h-0 flex-1 flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">No preview yet. Click Generate.</p>
      </div>
    )
  }

  if (preview.leads.length === 0) {
    return (
      <div className="min-h-0 flex-1 flex items-center justify-center py-16">
        <div className="text-center max-w-md">
          <p className="text-sm font-medium">No leads to preview</p>
          <p className="text-xs text-muted-foreground mt-1">
            This campaign has no leads, or every lead has been manually handled by you.
          </p>
        </div>
      </div>
    )
  }

  // Two siblings — counts bar (auto height) + scrolling list (flex-1).
  // The Fragment lets both be direct children of DialogContent's flex column,
  // matching the working create-campaign-wizard pattern.
  return (
    <>
      <div className="flex items-center justify-between text-xs text-muted-foreground border-b pb-2 shrink-0 gap-4">
        <span className="min-w-0 truncate">
          Showing{' '}
          <span className="font-medium text-foreground">{preview.lead_count_previewed}</span>
          {' '}of{' '}
          <span className="font-medium text-foreground">{preview.lead_count_total}</span>
          {' '}leads · {preview.step_count} steps each
        </span>
        <span className="shrink-0">
          {preview.cached ? 'From cache · ' : 'Generated · '}
          {fmtLocale(preview.generated_at, 'time')}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3 pb-2">
          {preview.leads.map((lead) => (
            <LeadPanel key={lead.lead_id} campaignId={campaignId} lead={lead} />
          ))}
        </div>
      </div>
    </>
  )
}

// ============================================================
// Per-lead panel — outbound preview + reply simulator
// ============================================================

function LeadPanel({ campaignId, lead }: { readonly campaignId: string; readonly lead: PreviewLead }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-lg border bg-card overflow-hidden min-w-0">
      {/* Header — always visible */}
      <button
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors min-w-0"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <p className="font-medium text-sm truncate min-w-0">{lead.name}</p>
            {lead.linkedin_url && (
              <a
                href={lead.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-muted-foreground hover:text-foreground shrink-0"
                aria-label="Open LinkedIn profile"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {[lead.title, lead.company].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] shrink-0">
          {lead.steps.length} message{lead.steps.length === 1 ? '' : 's'}
        </Badge>
      </button>

      {expanded && (
        <div className="border-t px-4 py-3 flex flex-col gap-4 bg-background min-w-0">
          <SequenceList lead={lead} />
          <Separator />
          <AIContextPanel lead={lead} />
          <Separator />
          <ReplySimulator campaignId={campaignId} lead={lead} />
        </div>
      )}
    </div>
  )
}

// ============================================================
// AI Context panel — what the AI saw when generating this lead's sequence
// ============================================================
//
// Eli 2026-06-03: prove the lead's About / posts / FAQ / objections / voice
// actually reached the prompt. The panel renders every input the backend
// loaded, with a "loaded / empty / missing" status badge per section. The
// raw assembled system + user prompts are tucked inside a collapsible at
// the bottom for the cases where the user wants to read what the model
// literally saw (skill.md content is elided server-side to bound the
// payload — it's a 1493-line file).

function AIContextPanel({ lead }: { readonly lead: PreviewLead }) {
  const [open, setOpen] = useState(false)
  const [showRaw, setShowRaw] = useState(false)
  const ctx = lead.ai_context

  // Cached previews from before the AI Context feature shipped don't have
  // ai_context. Render a small affordance telling the user to regenerate.
  if (!ctx) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Brain className="h-3.5 w-3.5" />
        <span>
          AI context not in this cached preview. Click <strong>Regenerate</strong> at the top to see
          exactly what the AI received.
        </span>
      </div>
    )
  }

  // Quick summary for the always-visible header: how many sections loaded
  // vs missing / empty. Tells the operator at a glance whether the AI was
  // working from a complete picture or a partial one.
  const loadedCount = ctx.sections.filter((s) => s.status === 'loaded').length
  const totalCount = ctx.sections.length

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-left hover:bg-muted/40 -mx-2 px-2 py-1 rounded-md transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <Brain className="h-4 w-4 shrink-0 text-[var(--color-cobalt-bright)]" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          AI context
        </span>
        <Badge variant="outline" className="text-[10px] ml-1">
          {loadedCount}/{totalCount} sources loaded
        </Badge>
      </button>

      {open && (
        <div className="flex flex-col gap-2 pl-6 min-w-0">
          <p className="text-xs text-muted-foreground">
            Every input the AI received when generating this lead's sequence — including SKILL.md,
            the lead's LinkedIn About + posts (when present), your offer / voice / message preferences,
            and the FAQs + objections the reply pipeline will use. Sections marked{' '}
            <span className="text-emerald-400 font-semibold">loaded</span> reached the prompt; sections
            marked <span className="text-amber-400 font-semibold">empty</span> /{' '}
            <span className="text-rose-400 font-semibold">missing</span> are gaps you may want to fill.
          </p>

          <ul className="flex flex-col gap-1.5">
            {ctx.sections.map((section) => (
              <li key={section.key}>
                <AIContextSectionRow section={section} />
              </li>
            ))}
          </ul>

          {(ctx.system_prompt_first_step || ctx.user_prompt_first_step) && (
            <div className="flex flex-col gap-1.5 mt-2">
              <button
                type="button"
                className="text-[11px] underline-offset-2 hover:underline text-[var(--color-cobalt-bright)] self-start"
                onClick={() => setShowRaw((v) => !v)}
              >
                {showRaw ? 'Hide' : 'Show'} the literal assembled prompt for the first step
              </button>
              {showRaw && (
                <div className="flex flex-col gap-2">
                  {ctx.system_prompt_first_step && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                        System prompt (skill.md content replaced with a placeholder — see /admin/skills-lab to view it in full)
                      </p>
                      <pre className="max-h-72 overflow-auto rounded-md border bg-muted/30 p-2 text-[11px] leading-snug whitespace-pre-wrap break-words">
                        {ctx.system_prompt_first_step}
                      </pre>
                    </div>
                  )}
                  {ctx.user_prompt_first_step && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                        User prompt
                      </p>
                      <pre className="max-h-40 overflow-auto rounded-md border bg-muted/30 p-2 text-[11px] leading-snug whitespace-pre-wrap break-words">
                        {ctx.user_prompt_first_step}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AIContextSectionRow({ section }: { readonly section: PreviewAIContextSection }) {
  const [expanded, setExpanded] = useState(false)
  const Icon =
    section.status === 'loaded' ? CheckCircle2 : section.status === 'empty' ? CircleSlash : XCircle
  const iconColor =
    section.status === 'loaded'
      ? 'text-emerald-400'
      : section.status === 'empty'
        ? 'text-amber-400'
        : 'text-rose-400'

  return (
    <div className="flex flex-col gap-1 rounded-md border bg-card/40 px-3 py-2 min-w-0">
      <div className="flex items-start gap-2 min-w-0">
        <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium">{section.label}</span>
            <Badge variant="outline" className="text-[9px] uppercase">
              {section.status}
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground break-words leading-snug mt-0.5">
            {section.detail}
          </p>
        </div>
        {section.excerpt && (
          <button
            type="button"
            className="text-[10px] text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'hide' : 'sample'}
          </button>
        )}
      </div>
      {expanded && section.excerpt && (
        <pre className="mt-1 max-h-40 overflow-auto rounded bg-muted/30 p-2 text-[11px] leading-snug whitespace-pre-wrap break-words">
          {section.excerpt}
        </pre>
      )}
    </div>
  )
}

// ============================================================
// Outbound sequence list
// ============================================================

function SequenceList({ lead }: { readonly lead: PreviewLead }) {
  return (
    <div className="flex flex-col gap-2 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Outbound sequence
      </p>
      <div className="flex flex-col gap-2">
        {lead.steps.map((step, i) => (
          <div key={i} className="flex gap-3 min-w-0">
            <div className="flex flex-col items-center pt-1 shrink-0">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-cobalt-bright">
                {i + 1}
              </div>
              {i < lead.steps.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-semibold">
                  {STEP_TYPE_LABELS[step.type] ?? step.type}
                </span>
                <Badge variant="secondary" className="text-[10px]">
                  {STRATEGY_LABELS[step.strategy] ?? step.strategy}
                </Badge>
              </div>
              {step.no_body ? (
                <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-xs italic text-muted-foreground">
                  Plain LinkedIn invitation — no message attached.
                </div>
              ) : step.error ? (
                <p className="text-xs text-destructive italic break-words">
                  Generation failed: {step.error}
                </p>
              ) : step.message ? (
                <div className="rounded-md bg-muted/40 px-3 py-2 text-sm whitespace-pre-wrap break-words">
                  {step.message}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No message returned.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================
// Reply simulator — multi-turn back-and-forth
// ============================================================

type Turn =
  | { readonly id: string; readonly from: 'them'; readonly content: string }
  | {
      readonly id: string
      readonly from: 'you'
      readonly content: string
      readonly classification?: SimulateReplyResponse['classification']
      readonly classificationError?: string
    }

function ReplySimulator({ campaignId, lead }: { readonly campaignId: string; readonly lead: PreviewLead }) {
  const [draft, setDraft] = useState('')
  const [turns, setTurns] = useState<readonly Turn[]>([])
  const simulate = useSimulateReply()
  const scrollRef = useRef<HTMLDivElement>(null)

  // Bottom of preview seed: the last AI message in the outbound sequence so
  // the simulator picks up where the preview left off.
  const seedAiMessage = useMemo(() => {
    const last = [...lead.steps].reverse().find((s) => s.message)
    return last?.message ?? null
  }, [lead.steps])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [turns, simulate.isPending])

  async function handleSend() {
    const text = draft.trim()
    if (!text || simulate.isPending) return

    // Build the conversation payload: every prior simulated turn + the
    // seeded outbound preview as if it was the last message you sent.
    const conversation: SimulatorMessage[] = []
    if (seedAiMessage) {
      conversation.push({ from: 'you', content: seedAiMessage })
    }
    for (const t of turns) {
      conversation.push({ from: t.from === 'them' ? 'them' : 'you', content: t.content })
    }

    const turnId = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    setTurns((prev) => [...prev, { id: turnId, from: 'them', content: text }])
    setDraft('')

    try {
      const result = await simulate.mutateAsync({
        campaignId,
        leadId: lead.lead_id,
        conversation,
        simulatedReply: text,
      })

      const replyId = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      if (result.ai_reply) {
        setTurns((prev) => [
          ...prev,
          {
            id: replyId,
            from: 'you',
            content: result.ai_reply!,
            classification: result.classification ?? undefined,
            classificationError: result.classification_error,
          },
        ])
      } else {
        // Server returned 200 but with no ai_reply (generation upstream failed
        // or returned empty). Surface the failure inline so it's obvious.
        const errMsg = result.ai_reply_error ?? 'no content returned by AI'
        console.warn('[simulate-reply] ai_reply was null:', errMsg, result)
        setTurns((prev) => [
          ...prev,
          {
            id: replyId,
            from: 'you',
            content: `[AI did not generate a reply — ${errMsg}]`,
            classification: result.classification ?? undefined,
            classificationError: result.classification_error,
          },
        ])
      }
    } catch (err) {
      // Network failure, 4xx/5xx, or server validation error.
      console.error('[simulate-reply] mutation threw:', err)
      const replyId = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const msg = err instanceof Error ? err.message : 'Simulation failed'
      setTurns((prev) => [
        ...prev,
        { id: replyId, from: 'you', content: `[Simulator error: ${msg}]` },
      ])
    }
  }

  function handleReset() {
    setTurns([])
    setDraft('')
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Reply simulator
        </p>
        {turns.length > 0 && (
          <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={handleReset}>
            Reset
          </Button>
        )}
      </div>

      <div ref={scrollRef} className="max-h-72 overflow-y-auto rounded-md border bg-muted/20 p-3 flex flex-col gap-2">
        {turns.length === 0 && !simulate.isPending ? (
          <p className="text-xs text-muted-foreground italic">
            Type a message below as if it came from {lead.name.split(' ')[0]}, then watch how the AI responds.
          </p>
        ) : (
          turns.map((turn) => <TurnBubble key={turn.id} turn={turn} leadName={lead.name} />)
        )}
        {simulate.isPending && (
          <div className="flex items-center gap-2 self-end text-xs text-muted-foreground py-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            AI is responding…
          </div>
        )}
      </div>

      <div className="flex gap-2 items-end">
        <textarea
          className="flex-1 min-h-[44px] max-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder={`Type a reply as ${lead.name.split(' ')[0]}…`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void handleSend()
            }
          }}
          disabled={simulate.isPending}
        />
        <Button
          size="sm"
          onClick={handleSend}
          disabled={!draft.trim() || simulate.isPending}
          className="h-[44px] px-3"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

function TurnBubble({ turn, leadName }: { readonly turn: Turn; readonly leadName: string }) {
  if (turn.from === 'them') {
    return (
      <div className="flex flex-col gap-0.5 items-start max-w-[85%] min-w-0">
        <span className="text-[10px] text-muted-foreground px-1 truncate max-w-full">
          {leadName.split(' ')[0]} (simulated)
        </span>
        <div className="rounded-2xl rounded-tl-sm bg-muted px-3 py-2 text-sm whitespace-pre-wrap break-words max-w-full">
          {turn.content}
        </div>
      </div>
    )
  }

  const cls = turn.classification
  return (
    <div className="flex flex-col gap-0.5 items-end self-end max-w-[85%] min-w-0">
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground px-1 flex-wrap justify-end">
        <span>AI reply</span>
        {cls?.category && (
          <Badge variant="outline" className="text-[10px] capitalize">
            {String(cls.category).replace(/_/g, ' ')}
            {typeof cls.confidence === 'number' ? ` · ${cls.confidence}%` : ''}
          </Badge>
        )}
      </div>
      <div className="rounded-2xl rounded-tr-sm bg-primary/10 px-3 py-2 text-sm whitespace-pre-wrap break-words max-w-full">
        {turn.content}
      </div>
      {turn.classificationError && (
        <span className="text-[10px] text-destructive px-1 italic break-words max-w-full">
          classification: {turn.classificationError}
        </span>
      )}
    </div>
  )
}
